import './i18n';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './components/LanguageSelector';
import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import { FaUpload, FaCheck, FaTimes, FaSpinner, FaDownload } from "react-icons/fa";
import { FaShareNodes, FaEnvelope } from "react-icons/fa6";
import { TwitterShareButton, FacebookShareButton, WhatsappShareButton, EmailShareButton } from "react-share";
import { FaTwitter, FaFacebook, FaWhatsapp } from "react-icons/fa";
import "./App.css";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import PaymentPage from './components/PaymentPage';
import AdvancedAnalysisPage from './components/AdvancedAnalysisPage';
import DisclaimerPage from './components/DisclaimerPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import CertificateVerification from './components/CertificateVerification';
import pleasantSound from './sounds/pleasant.mp3';
import unpleasantSound from './sounds/unpleasant.mp3';
import MembersArea from './MembersArea'; // Added import for MembersArea
import Breadcrumbs from './components/Breadcrumbs'; // Added import for Breadcrumbs
import Header from './components/Header'; // Added import for Header
import CookiePolicyPage from './components/CookiePolicyPage';
import CookieBanner from './components/CookieBanner';
import CookieSettingsModal from './components/CookieSettingsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const stripePromise = loadStripe('your-publishable-key-here');

// Hero section component
const Hero = () => {
  const { t } = useTranslation();

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-900/70 to-black/70 z-10"></div>
      <div className="hero-slider">
        <div className="hero-slide" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1730757679771-b53e798846cf')" }}></div>
        <div className="hero-slide" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1619134778706-7015533a6150')" }}></div>
        <div className="hero-slide" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1698612059734-280fed56f417')" }}></div>
      </div>
      <div className="relative z-20 container mx-auto px-6 py-32 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          {t('verify_title')}
        </h1>
        <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-3xl mx-auto">
          {t('verify_description')}
        </p>
        <button
          onClick={() => document.getElementById('upload-section').scrollIntoView({ behavior: 'smooth' })}
          className="bg-gold text-black font-bold py-4 px-8 rounded-lg hover:bg-gold/80 transition duration-300 text-lg"
        >
          {t('start_free_scan_now')}
        </button>
      </div>
    </div>
  );
};

// Brand logos component
const BrandLogos = () => {
  const { t } = useTranslation();
  const logos = [
    { name: "Rolex", image: "https://images.unsplash.com/photo-1706801803974-fc52c9aaac6c" },
    { name: "Omega", image: "https://images.unsplash.com/photo-1573048338291-70f6fe5bfe25" },
    { name: t("Covers most Luxury Brands like Patek Philippe, Audemars Piguet, Richard Mille, Jaeger-LeCoultre, A. Lange & Söhne, Cartier, most Japanese and many other well known Brands..."), image: "https://images.unsplash.com/photo-1588599037715-98324a65c8fb" }
  ];
  
  return (
    <div className="bg-gray-100 py-16">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-12">{t('trusted_by_watch_enthusiasts')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {logos.map((logo, index) => (
            <div key={index} className="brand-logo-card">
              <div className="h-32 w-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-gold flex items-center justify-center bg-white">
                <img 
                  src={logo.image} 
                  alt={logo.name} 
                  className="w-full h-full object-contain"
                />
              </div>
              <p className="text-center font-semibold text-xl">{logo.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// File upload component
const UploadSection = ({ onAnalysisComplete }) => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stageText, setStageText] = useState('');
  const videoRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [isFrontCamera, setIsFrontCamera] = useState(false);

  const stages = [
    "Analyzing Brand and Logo Proportions",
    "Analyzing the Dial Design and Layout",
    "Analyzing Hour Marker Style",
    "Analyzing Color Scheme and Materials",
    "Analyzing Bezel",
    "Analyzing Bracelet",
    "Analyzing Case Finish and Craftsmanship",
    "Analyzing Rehaut (Inner Bezel Ring)",
    "Performing Additional Authenticity Checks",
    "Finalizing"
  ];

  const startCamera = (useFrontCamera = false) => {
    setCameraActive(true);
    const constraints = {
      video: {
        facingMode: useFrontCamera ? 'user' : 'environment',
      },
    };
    navigator.mediaDevices.getUserMedia(constraints)
      .then((stream) => {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setTimeout(() => {
          window.scrollBy({ top: 300, behavior: 'smooth' });
        }, 500);
      })
      .catch((err) => {
        console.error("Error accessing camera:", err);
        setCameraActive(false);
      });
  };

  const toggleCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    const newCameraMode = !isFrontCamera;
    setIsFrontCamera(newCameraMode);
    startCamera(newCameraMode);
  };

  const resizeImage = (imageBlob, targetSize = { width: 1024, height: 1024 }) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetSize.width;
        canvas.height = targetSize.height;
        const ctx = canvas.getContext('2d');

        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Calculate aspect ratio and center the image
        const scale = Math.min(
          targetSize.width / img.width,
          targetSize.height / img.height
        );
        const x = (targetSize.width - img.width * scale) / 2;
        const y = (targetSize.height - img.height * scale) / 2;
        ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

        canvas.toBlob((resizedBlob) => {
          resolve(resizedBlob);
        }, 'image/jpeg', 0.95);
      };
      img.src = URL.createObjectURL(imageBlob);
    });
  };

  const capturePhoto = async () => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      const resizedBlob = await resizeImage(blob);
      const file = new File([resizedBlob], 'captured-image.jpg', { type: 'image/jpeg' });
      setFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
      setCameraActive(false);
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    });
  };

  const onDrop = async (acceptedFiles) => {
    if (cameraActive && videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setCameraActive(false);
    }
    const selectedFile = acceptedFiles[0];
    const resizedBlob = await resizeImage(selectedFile);
    const resizedFile = new File([resizedBlob], selectedFile.name, { type: selectedFile.type });
    setFile(resizedFile);
    const previewUrl = URL.createObjectURL(resizedFile);
    setPreview(previewUrl);
    setUploadError(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp']
    },
    maxFiles: 1
  });

  const handleUpload = async () => {
    if (!file) {
      setUploadError(t("please_select_image"));
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    // Simulate progress bar with stage updates
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }

        const newProgress = prev + 1;
        const stageIndex = Math.floor((newProgress / 100) * stages.length);
        setStageText(stages[stageIndex] || '');

        return newProgress;
      });
    }, Math.random() * (300 - 250) + 250); // Random delay between 25-30 seconds

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/analyze-watch`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Pass the analysis result to the parent component
      onAnalysisComplete(response.data);

      // Scroll to results
      document.getElementById('results-section').scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      console.error("Error uploading image:", error);
      let errorMessage = t("failed_to_analyze");

      if (error.response) {
        if (error.response.status === 400 && error.response.data.detail === "Multiple Image Error!") {
          errorMessage = "Multiple Image Error!";
        } else if (error.response.status === 500) {
          errorMessage = t("server_error_analyzing");
        }
      }

      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const clearUpload = () => {
    setFile(null);
    setPreview(null);
    setUploadError(null);
  };
  
  const clearAndRetake = () => {
    setFile(null);
    setPreview(null);
    setUploadError(null);
    startCamera();
  };
  
  return (
    <div id="upload-section" className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-2">{t('upload_title')}</h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          {t('upload_hint')}
        </p>
        
        <div className="max-w-2xl mx-auto">
          {!preview ? (
            <div>
              <div 
                {...getRootProps()} 
                className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition duration-300
                  ${isDragActive ? 'border-gold bg-gold/10' : 'border-gray-300 hover:border-gold/50 hover:bg-gray-100'}`}
              >
                <input {...getInputProps()} />
                <FaUpload className="mx-auto text-4xl text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">
                  {isDragActive
                    ? t("drop_here")
                    : t("drag_drop_or_click")}
                </p>
                <p className="text-gray-500 text-sm">
                  {t("upload_support")}
                </p>
              </div>
              <div className="text-center mt-4 flex justify-center space-x-4">
                <button 
                  onClick={() => startCamera(isFrontCamera)}
                  className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition w-40"
                >
                  {t("Use Your Camera")}
                </button>
                {cameraActive && (
                  <>
                    <button 
                      onClick={toggleCamera}
                      className="bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition w-40"
                    >
                      {t("Switch")}
                    </button>
                    <button 
                      onClick={() => {
                        setCameraActive(false);
                        if (videoRef.current && videoRef.current.srcObject) {
                          videoRef.current.srcObject.getTracks().forEach(track => track.stop());
                          videoRef.current.srcObject = null;
                        }
                      }}
                      className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition w-40"
                    >
                      {t("Turn Off Camera")}
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="border rounded-lg p-6 bg-white shadow-md">
              <div className="mb-4 relative">
                <img 
                  src={preview} 
                  alt={t("watch_preview_alt")} 
                  className="w-full rounded-lg object-contain max-h-[400px]"
                />
                <button 
                  onClick={clearUpload}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition"
                  aria-label={t("clear_upload")}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="text-center">
                <p className="text-gray-700 mb-4">{file.name}</p>
                <div className="flex justify-center space-x-4">
                  <button 
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="bg-blue-600 text-white py-3 px-8 rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isUploading ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        <span>{t("analyzing")}</span>
                      </>
                    ) : (
                      <>
                        <span>{t("verify_authenticity")}</span>
                      </>
                    )}
                  </button>
                  <button 
                    onClick={clearAndRetake}
                    className="bg-red-600 text-white py-3 px-8 rounded-lg hover:bg-red-700 transition"
                  >
                    {t("Retake Photo")}
                  </button>
                </div>
                {uploadError && (
                  <p className="text-red-500 mt-4">{uploadError}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {cameraActive && (
          <div className="mt-6 text-center">
            <video ref={videoRef} className="w-full max-w-md mx-auto border rounded-lg" />
            <div className="flex justify-center gap-4 mt-4">
              <button 
                onClick={capturePhoto}
                className="bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition"
              >
                {t("Capture Photo")}
              </button>
              <button 
                onClick={toggleCamera}
                className="bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition"
              >
                {isFrontCamera ? t("Switch to Back Camera") : t("Switch to Front Camera")}
              </button>
            </div>
          </div>
        )}

        {isUploading && (
          <div className="mt-6">
            <p className="text-blue-600 mb-2">{stageText}</p>
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div
                className="bg-blue-600 h-4 rounded-full"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Results display component
const ResultsSection = ({ analysisResult }) => {
  const { t } = useTranslation();
  const [isLoadingComparisons, setIsLoadingComparisons] = useState(false);

  useEffect(() => {
    if (analysisResult) {
      setIsLoadingComparisons(true);
      setTimeout(() => {
        setIsLoadingComparisons(false);
      }, 1500);

      // Play sound based on authenticity
      const audio = new Audio(
        analysisResult.is_authentic ? pleasantSound : unpleasantSound
      );
      audio.play();
    }
  }, [analysisResult]);

  if (!analysisResult) return null;

  const {
    watch_model,
    is_authentic,
    confidence_score,
    analysis_details,
    comparison_images
  } = analysisResult;

  const statusText = is_authentic === true
    ? t("authentic")
    : is_authentic === false
      ? t("likely_fake")
      : t("inconclusive");
  const statusColor = is_authentic ? "text-green-600" : is_authentic === false ? "text-red-600" : "text-yellow-600";
  const statusBg = is_authentic ? "bg-green-100" : is_authentic === false ? "bg-red-100" : "bg-yellow-100";
  const overlayText = is_authentic
    ? { small: "most likely", large: "GENUINE WATCH", color: "text-green-600" }
    : is_authentic === false
      ? { small: "most likely", large: "FAKE WATCH", color: "text-red-600" }
      : { small: "Analysis", large: "Inconclusive", color: "text-yellow-600" };

  const DownloadCertificate = ({ analysisId }) => {
    const { t } = useTranslation();
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadStatus, setDownloadStatus] = useState(null);

    const handleDownload = async () => {
      setIsDownloading(true);
      setDownloadStatus(null);
      
      try {
        const response = await fetch(`${API}/generate-certificate/${analysisId}`, {
          method: 'GET',
          responseType: 'blob'
        });

        if (!response.ok) {
          throw new Error('Failed to generate certificate');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'watch_authenticity_certificate.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setDownloadStatus('success');
      } catch (error) {
        console.error('Error downloading certificate:', error);
        setDownloadStatus('error');
      } finally {
        setIsDownloading(false);
        // Auto-hide status after 3 seconds
        setTimeout(() => setDownloadStatus(null), 3000);
      }
    };

    return (
      <div>
        <button
          onClick={handleDownload}
          disabled={isDownloading}
          className={`bg-gold text-black font-bold py-2 px-4 rounded-lg hover:bg-gold/80 transition duration-300 flex items-center gap-2 ${
            isDownloading ? 'opacity-75 cursor-not-allowed' : ''
          }`}
          title={t('download_certificate_tooltip')}
        >
          {isDownloading ? (
            <>
              <FaSpinner className="animate-spin" />
              {t('certificate_generating')}
            </>
          ) : (
            <>
              <FaDownload />
              {t('download_certificate')}
            </>
          )}
        </button>
        
        {downloadStatus && (
          <div className={`mt-2 p-2 rounded-lg text-sm flex items-center gap-2 ${
            downloadStatus === 'success' 
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {downloadStatus === 'success' ? (
              <>
                <FaCheck />
                {t('certificate_download_success')}
              </>
            ) : (
              <>
                <FaTimes />
                {t('certificate_download_error')}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div id="results-section" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-16">{t("authentication_results")}</h2>

        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
          <div className={`p-6 ${statusBg} flex items-center justify-between`}>
            <div className="flex items-center space-x-3">
              <h3 className={`text-2xl font-bold ${statusColor}`}>{statusText}</h3>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">{t("confidence_score")}</p>
              <p className="text-xl font-bold">{confidence_score || t("not_available")}%</p>
            </div>
          </div>

          <div className="relative">
            <img
              src={comparison_images?.[0] || ""}
              alt={t("watch_preview_alt")}
              className="w-full h-auto object-cover"
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50">
              <p className="text-white text-sm font-medium mb-2">{overlayText.small}</p>
              <p className={`text-4xl font-bold ${overlayText.color}`}>{overlayText.large}</p>
            </div>
          </div>

          <div className="p-6 border-b">
            <h4 className="text-lg font-semibold mb-2">{t("watch_identification")}</h4>
            <p className="text-gray-700">{watch_model || t("unable_to_identify")}</p>
          </div>

          <div className="p-6 border-b">
            <h4 className="text-lg font-semibold mb-2">{t("detailed_analysis")}</h4>
            <p className="text-gray-700 whitespace-pre-line">{analysis_details || t("no_analysis")}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Feature highlights
const Features = () => {
  const { t } = useTranslation();
  const features = [
    {
      title: t("ai_powered_analysis_title"),
      description: t("ai_powered_analysis_desc")
    },
    {
      title: t("detailed_reports_title"),
      description: t("detailed_reports_desc")
    },
    {
      title: t("genuine_comparisons_title"),
      description: t("genuine_comparisons_desc")
    },
    {
      title: t("share_results_title"),
      description: t("share_results_desc")
    },
  ];
  
  return (
    <div className="py-20 bg-blue-900 text-white">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-12">{t("advanced_auth_features")}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="border border-blue-700 rounded-lg p-6 bg-blue-800 hover:bg-blue-700 transition duration-300">
              <h3 className="text-xl font-bold mb-3 text-gold">{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Home page component
const Home = () => {
  const { t } = useTranslation();
  const [analysisResult, setAnalysisResult] = useState(null);
  const [cookieModalOpen, setCookieModalOpen] = useState(false);
  
  useEffect(() => {
    // You could fetch previous analyses here or other data
  }, []);
  
  const handleAnalysisComplete = (result) => {
    setAnalysisResult(result);
  };
  
  const downloadCertificate = async () => {
    const response = await fetch('/api/generate-certificate');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = 'certificate.pdf';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  return (
    <div>
      <Header />
      <Hero />
      <BrandLogos />
      <Features />
      <UploadSection onAnalysisComplete={handleAnalysisComplete} />
      <ResultsSection analysisResult={analysisResult} />
      {/* <div className="text-center my-8"> // Commenting out or removing the old certificate download button if Footer will handle similar links or if it's redundant
        <button 
          onClick={downloadCertificate}
          className="bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition"
        >
          {t("Download PDF Certificate")}
        </button>
      </div> */}
      <Footer />
      <CookieBanner />
      <button onClick={() => setCookieModalOpen(true)} className="fixed bottom-4 right-4 z-50 bg-gray-800 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-gray-700">{t('cookie_settings')}</button>
      <CookieSettingsModal open={cookieModalOpen} onClose={() => setCookieModalOpen(false)} />
    </div>
  );
};

// Footer component
const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="bg-gray-800 text-white py-12">
      <div className="container mx-auto px-6">
        {/* Grid will have 2 children: title section (1 col) and legal links section (3 cols) */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div className="md:col-span-1"> {/* Title section: takes 1 of 4 columns on md+ screens */}
            <h5 className="text-xl font-bold mb-4">{t('footer_title')}</h5>
            <p className="text-gray-400">{t('footer_powered_by')}</p>
          </div>
          {/* The two placeholder divs that were previously here are removed to allow legal links to span more columns */}
          <div className="md:col-span-3"> {/* Legal links section: takes 3 of 4 columns on md+ screens */}
            <ul className="flex flex-wrap md:flex-nowrap items-center justify-start md:justify-end space-x-3">
              <li><Link to="/terms-of-service" className="hover:text-gold transition whitespace-nowrap">{t('terms_of_service')}</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-gold transition whitespace-nowrap">{t('privacy_policy')}</Link></li>
              <li><Link to="/contact-us" className="hover:text-gold transition whitespace-nowrap">{t('contact_us')}</Link></li>
              <li><Link to="/disclaimer" className="hover:text-gold transition whitespace-nowrap">{t('disclaimer')}</Link></li>
              <li><Link to="/cookie-policy" className="hover:text-gold transition whitespace-nowrap">{t('cookie_policy')}</Link></li> {/* Added Cookie Policy link */}
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-8 text-center">
          <p className="text-gray-500">&copy; {new Date().getFullYear()} {t('footer_copyright')}</p>
        </div>
      </div>
    </footer>
  );
};

// Main App component
function App() {
  const location = useLocation();

  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    let breadcrumbs = [{ name: t('Home'), path: '/' }];

    pathnames.forEach((value, index) => {
      const to = `/${pathnames.slice(0, index + 1).join('/')}`;
      // Capitalize first letter and replace hyphens with spaces for display name
      const displayName = value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
      if (value === 'members-area') {
        breadcrumbs.push({ name: t('Members Area'), path: to });
      } else {
        // You can add more specific names here if needed
        breadcrumbs.push({ name: t(displayName) || displayName, path: to });
      }
    });
    return breadcrumbs;
  };
  
  // Translation hook for breadcrumbs
  const { t } = useTranslation();


  return (
    <div className="App">
      {/* Header is rendered by Home and potentially MembersArea, or could be lifted here */}
      {/* <Header /> */}
      <Breadcrumbs breadcrumbs={getBreadcrumbs()} />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/members-area" element={<MembersArea />} /> {/* Added route for MembersArea */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/payment" element={
          <Elements stripe={stripePromise}>
            <PaymentPage />
          </Elements>
        } />
        <Route path="/advanced-analysis" element={<AdvancedAnalysisPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/verify-certificate/:certId?" element={<CertificateVerification />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} /> {/* Added CookiePolicyPage route */}
      </Routes>
    </div>
  );
}

// Wrap App with BrowserRouter so useLocation can be used in App
const WrappedApp = () => (
  <BrowserRouter>
    <App />
  </BrowserRouter>
);

export default WrappedApp;